/**
 * PATH: packages/ui/src/kanban/components/settings-home/FaceRecipeSettings.tsx
 * FILE_ID: UI-KANBAN-SETTINGS-FACE-RECIPE-001
 * PURPOSE: Field / rail / strip / menu / extras / chrome mode editors.
 * ROLE IN ARCHITECTURE: Settings-home leaf over cardFaceRecipe catalogs.
 * MAIN EXPORTS: FaceRecipeSettings
 * NON-RESPONSIBILITIES: Does not persist. Phone preview is local only.
 * NOTES FOR FUTURE AI: tileOpen expand never disables host open. Do not
 *   render a field row for `due` or a rail row for `allocator` —
 *   FaceSettingsPage owns those Settings testids.
 */
import { useState } from 'react'

import type { KanbanCardFacePrefs, ResolvedCardFacePrefs } from '../board-view/cardFacePrefs'
import {
  CARD_FACE_EXTRA_IDS,
  CARD_FACE_FIELD_IDS,
  CARD_FACE_MENU_IDS,
  CARD_FACE_RAIL_IDS,
  CARD_FACE_STRIP_IDS,
  EXTRA_LABELS,
  FIELD_LABELS,
  MENU_LABELS,
  RAIL_LABELS,
  STRIP_LABELS,
  menuShows,
  type CardFaceCompleteMode,
  type CardFaceComposeSeat,
  type CardFacePlaceMode,
  type CardFaceRailMode,
  type CardFaceTileBody,
  type CardFaceTileOpen,
} from '../board-view/cardFaceRecipe'
import { FacePreview } from './FacePreview'
import { OptionStrip, SectionShell } from './chrome'
import {
  faceChromeTestId,
  faceExtraTestId,
  faceFieldTestId,
  faceMenuTestId,
  faceRailTestId,
  faceStripTestId,
} from './settingsTestIds'

const PLACE_OPTIONS = [
  { id: 'rail' as const, title: 'On rail', description: 'Shows on the hover rail.' },
  { id: 'menu' as const, title: 'More menu', description: 'Right-click / more only.' },
  { id: 'off' as const, title: 'Off', description: 'Hidden. Card data stays.' },
]

function SwitchRow({
  label,
  checked,
  testId,
  onToggle,
}: {
  label: string
  checked: boolean
  testId?: string
  onToggle: () => void
}) {
  return (
    <label className="flex items-center justify-between gap-3 py-1 text-xs text-neutral-600 dark:text-zinc-300">
      {label}
      <button
        type="button"
        role="switch"
        data-testid={testId}
        aria-checked={checked}
        onClick={onToggle}
        className={[
          'relative inline-flex h-5 w-9 shrink-0 items-center rounded-full',
          checked ? 'bg-blue-600' : 'bg-neutral-300 dark:bg-zinc-700',
        ].join(' ')}
      >
        <span
          className={[
            'inline-block h-4 w-4 transform rounded-full bg-white shadow',
            checked ? 'translate-x-4' : 'translate-x-0.5',
          ].join(' ')}
        />
      </button>
    </label>
  )
}

export function FaceRecipeSettings({
  view,
  resolved,
  writeView,
}: {
  view: KanbanCardFacePrefs
  resolved: ResolvedCardFacePrefs
  writeView: (next: KanbanCardFacePrefs) => void
}) {
  const [phone, setPhone] = useState(false)
  return (
    <div data-testid="settings-face-recipe">
      <SectionShell
        title="Preview"
        hint="Phone is a local preview. It does not change how a card opens."
      >
        <OptionStrip
          label="Preview size"
          value={phone ? 'phone' : 'desktop'}
          options={[
            { id: 'desktop', title: 'Desktop', description: 'Wide tile.' },
            { id: 'phone', title: 'Phone', description: 'Narrow tile. Not persisted.' },
          ]}
          testId={faceChromeTestId('preview')}
          onChange={(id) => setPhone(id === 'phone')}
        />
        <FacePreview prefs={view} phone={phone} />
      </SectionShell>
      <SectionShell title="Tile fields" hint="Hide removes chrome. Title cannot hide.">
        {CARD_FACE_FIELD_IDS.filter((id) => id !== 'due').map((id) => (
          <SwitchRow
            key={id}
            label={FIELD_LABELS[id]}
            checked={resolved.fields[id] === true}
            testId={faceFieldTestId(id)}
            onToggle={() =>
              writeView({
                ...view,
                fields: { ...view.fields, [id]: !resolved.fields[id] },
              })
            }
          />
        ))}
      </SectionShell>
      <SectionShell title="Rail parks" hint="Off omits the handler. Also-show is a link.">
        {CARD_FACE_RAIL_IDS.filter((id) => id !== 'allocator').map((id) => (
          <OptionStrip
            key={id}
            label={RAIL_LABELS[id]}
            value={resolved.place[id] ?? 'rail'}
            options={PLACE_OPTIONS}
            testId={faceRailTestId(id)}
            onChange={(next: CardFacePlaceMode) =>
              writeView({
                ...view,
                place: { ...view.place, [id]: next },
              })
            }
          />
        ))}
      </SectionShell>
      <SectionShell title="Hover strip">
        {CARD_FACE_STRIP_IDS.map((id) => (
          <SwitchRow
            key={id}
            label={STRIP_LABELS[id]}
            checked={resolved.strip[id] === 'on'}
            testId={faceStripTestId(id)}
            onToggle={() =>
              writeView({
                ...view,
                strip: {
                  ...view.strip,
                  [id]: resolved.strip[id] === 'on' ? 'off' : 'on',
                },
              })
            }
          />
        ))}
      </SectionShell>
      <SectionShell title="Right-click menu" hint="Open workbook is a settings-gated menu id.">
        {CARD_FACE_MENU_IDS.map((id) => (
          <SwitchRow
            key={id}
            label={MENU_LABELS[id]}
            checked={menuShows(resolved, id)}
            testId={faceMenuTestId(id)}
            onToggle={() => {
              if (id === 'reply') {
                const on = menuShows(resolved, 'reply')
                writeView({
                  ...view,
                  compose: on ? view.compose : 'menu',
                  menu: { ...view.menu, reply: on ? 'off' : 'menu' },
                })
                return
              }
              writeView({
                ...view,
                menu: {
                  ...view.menu,
                  [id]: resolved.menu[id] === 'menu' ? 'off' : 'menu',
                },
              })
            }}
          />
        ))}
      </SectionShell>
      <SectionShell title="Extras">
        {CARD_FACE_EXTRA_IDS.map((id) => (
          <SwitchRow
            key={id}
            label={EXTRA_LABELS[id]}
            checked={resolved.extras[id] === 'on'}
            testId={faceExtraTestId(id)}
            onToggle={() =>
              writeView({
                ...view,
                extras: {
                  ...view.extras,
                  [id]: resolved.extras[id] === 'on' ? 'off' : 'on',
                },
              })
            }
          />
        ))}
      </SectionShell>
      <SectionShell
        title="Chrome"
        hint="Card height and the rail are separate. Expanded stays tall. The rail still follows Rail mode."
      >
        <OptionStrip
          label="Card expanded state"
          value={resolved.tileBody ?? 'rest'}
          options={[
            { id: 'rest', title: 'Hover', description: 'Short until you hover.' },
            { id: 'open', title: 'Expanded', description: 'Stays on the full face. Does not open the rail.' },
          ]}
          testId={faceChromeTestId('tileBody')}
          onChange={(id: CardFaceTileBody) => writeView({ ...view, tileBody: id })}
        />
        <OptionStrip
          label="Rail mode"
          value={resolved.railMode ?? 'hover'}
          options={[
            { id: 'hover', title: 'On hover', description: 'Default.' },
            { id: 'always', title: 'Always', description: 'Rail stays armed.' },
            { id: 'hidden', title: 'Hidden', description: 'No rail or strip. Right-click still works.' },
          ]}
          testId={faceChromeTestId('railMode')}
          onChange={(id: CardFaceRailMode) => writeView({ ...view, railMode: id })}
        />
        <OptionStrip
          label="Compose seat"
          value={resolved.compose ?? 'strip'}
          options={[
            { id: 'strip', title: 'Strip', description: 'Hover strip.' },
            { id: 'rail', title: 'Rail', description: 'Action rail.' },
            { id: 'menu', title: 'Menu', description: 'Right-click only.' },
            { id: 'off', title: 'Off', description: 'Hidden.' },
          ]}
          testId={faceChromeTestId('compose')}
          onChange={(id: CardFaceComposeSeat) => writeView({ ...view, compose: id })}
        />
        <OptionStrip
          label="Tile click extra"
          value={resolved.tileOpen ?? 'host'}
          options={[
            { id: 'host', title: 'Host open', description: 'Click still opens the drawer or popup.' },
            { id: 'expand', title: 'Also expand', description: 'Arms chrome. Does not disable host open.' },
          ]}
          testId={faceChromeTestId('tileOpen')}
          onChange={(id: CardFaceTileOpen) => writeView({ ...view, tileOpen: id })}
        />
        <OptionStrip
          label="Complete check"
          value={resolved.complete ?? 'hover'}
          options={[
            { id: 'hover', title: 'On hover', description: 'Default.' },
            { id: 'always', title: 'Always', description: 'Check stays visible.' },
            { id: 'off', title: 'Off', description: 'Hidden. Status stays on the card.' },
          ]}
          testId={faceChromeTestId('complete')}
          onChange={(id: CardFaceCompleteMode) => writeView({ ...view, complete: id })}
        />
      </SectionShell>
    </div>
  )
}
